import type { PlenaryActivityInformation } from '@hendrx/lms/contracts/server';

import { PLENARY_STUDENT_COOKIE_NAME } from './consts';

export const getPlenarySignUpCookieName = (activityInformation: PlenaryActivityInformation) => {
  const { type, id } = activityInformation;
  return `${PLENARY_STUDENT_COOKIE_NAME}_${type}_${id}`;
};
export const getPlenaryAuthenticateCookieName = (contextId: string) => {
  return `${PLENARY_STUDENT_COOKIE_NAME}_${contextId}`;
};

/** Matches the names {@link getPlenarySignUpCookieName} builds; group 2 is the draft id. */
const SIGN_UP_COOKIE_PATTERN = new RegExp(`^${PLENARY_STUDENT_COOKIE_NAME}_(regular|assignment)_(.+)$`);

/**
 * `cookie-parser` does NOT omit a cookie whose signature failed — it keeps the key and sets the
 * value to `false`. So presence of a key proves nothing; only a non-empty string value means the
 * server actually signed it. Skipping this check would let a client forge a cookie name.
 */
const isSignedCookieValue = (value: unknown): value is string => typeof value === 'string' && value !== '';

/**
 * Sign-up cookie tokens grouped by draft id. A draft can have both flows present at once; callers
 * verify each candidate so a stale/invalid token from one flow doesn't mask a valid one from the other.
 *
 * The per-draft candidate count is bounded by construction — cookie names are unique, and a draft has
 * exactly one name per lobby flow — so a caller that verifies one draft's tokens against the LMS makes
 * a bounded number of calls no matter how many cookies the client sends.
 *
 * Values are client-supplied: the signature proves this server issued the cookie, nothing more. Any
 * caller acting on the *contents* MUST verify the token via `serverModules.lms.authenticateForPlenary`.
 */
export const collectPlenarySignUpCookieTokens = (signedCookies: Record<string, unknown>): Map<string, string[]> => {
  const tokens = new Map<string, string[]>();
  for (const cookieName of Object.keys(signedCookies)) {
    const match = SIGN_UP_COOKIE_PATTERN.exec(cookieName);
    if (!match) continue;
    const value = signedCookies[cookieName];
    if (!isSignedCookieValue(value)) continue;
    const draftId = match[2];
    const existing = tokens.get(draftId);
    if (existing) existing.push(value);
    else tokens.set(draftId, [value]);
  }
  return tokens;
};

/**
 * Did this caller complete a lobby sign-up for *some* plenary? Proof of a real signup only — it says
 * nothing about *which* plenary they may access, which stays with the per-route access policy.
 */
export const hasPlenarySignUpCookie = (signedCookies: Record<string, unknown>): boolean =>
  Object.keys(signedCookies).some(
    cookieName => SIGN_UP_COOKIE_PATTERN.test(cookieName) && isSignedCookieValue(signedCookies[cookieName])
  );
